import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import PrintBridge from '../lib/PrintBridge';
import { Printer, FileText, ReceiptText, Minus, Plus, ChevronLeft, Loader2 } from 'lucide-react';

/**
 * Reprint modal — pick the document, then the number of copies, then print
 * each copy with a ~2s cut-delay between them. Only the official BIR invoice
 * (SI/CI) gets a "REPRINTED COPY #N" banner (copy #1 = the original); internal
 * Order Slips / Trust Agreements reprint clean.
 *
 * @param {Array<{label:string,type:string,sub?:string}>} options doc choices
 */
export default function ReprintModal({
  open, onClose, options = [], printData, docCode = '', businessInfo = {},
  backendUrl, defaultFormat = 'thermal',
}) {
  const [step, setStep] = useState('choose');
  const [chosen, setChosen] = useState(null);
  const [copies, setCopies] = useState(1);
  const [format, setFormat] = useState(defaultFormat);
  const [printing, setPrinting] = useState(false);
  const [progress, setProgress] = useState('');

  useEffect(() => {
    if (open) {
      setChosen(options.length === 1 ? options[0] : null);
      setStep(options.length === 1 ? 'copies' : 'choose');
      setCopies(1);
      setFormat(defaultFormat);
      setPrinting(false);
      setProgress('');
    }
  }, [open]); // eslint-disable-line

  const runPrint = async () => {
    if (!chosen) return;
    setPrinting(true);
    // Inter-copy timing is now sourced from a single helper. When the native
    // APK reports `awaitsCompletion: true` (post-Cursor-fix), the helper
    // returns just the 2 s cut buffer; otherwise it adds an estimated print
    // duration so copy #2 doesn't queue while copy #1 is still feeding paper.
    try {
      for (let i = 1; i <= copies; i++) {
        setProgress(`Printing copy ${i} of ${copies}…`);
        let copyNumber = 1;
        if (chosen.type === 'official_receipt' && docCode) {
          try {
            const r = await axios.post(`${backendUrl}/api/doc/${docCode}/register-print`, { doc_type: 'official_receipt' });
            copyNumber = r.data?.copy_number || 1;
          } catch { /* fall back to an unlabelled copy rather than block the print */ }
        }
        await PrintBridge.print({ type: chosen.type, data: printData, format, businessInfo, docCode, copyNumber });
        if (i < copies) {
          const gap = await PrintBridge.interCopyGapMs({ data: printData, format });
          setProgress(`Copy ${i} printing — cut the paper. Next copy in ~${Math.ceil(gap / 1000)}s…`);
          await new Promise((res) => setTimeout(res, gap));
        }
      }
      toast.success(`${copies} ${copies > 1 ? 'copies' : 'copy'} sent to the printer`);
      onClose();
    } catch (e) {
      toast.error('Print failed. Check the printer and try again.');
    }
    setPrinting(false);
    setProgress('');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !printing) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="reprint-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]">
            <Printer size={18} /> Reprint Document
          </DialogTitle>
        </DialogHeader>

        {printing ? (
          <div className="py-8 text-center space-y-3" data-testid="reprint-progress">
            <Loader2 size={28} className="animate-spin mx-auto text-[#1A4D2E]" />
            <p className="text-sm font-medium text-slate-700">{progress}</p>
            <p className="text-xs text-slate-400">Please wait — do not close this window.</p>
          </div>
        ) : step === 'choose' ? (
          <div className="space-y-2.5">
            <p className="text-xs text-slate-500">What would you like to reprint?</p>
            {options.map((opt) => (
              <button
                key={opt.type}
                type="button"
                data-testid={`reprint-choose-${opt.type}`}
                onClick={() => { setChosen(opt); setStep('copies'); }}
                className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-slate-200 hover:border-[#1A4D2E] hover:bg-emerald-50/50 transition-all text-left">
                {opt.type === 'official_receipt' ? <ReceiptText size={20} className="text-[#1A4D2E] shrink-0" /> : <FileText size={20} className="text-slate-500 shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{opt.label}</p>
                  {opt.sub && <p className="text-[11px] text-slate-400">{opt.sub}</p>}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {options.length > 1 && (
              <button type="button" onClick={() => setStep('choose')} className="text-xs text-slate-400 flex items-center gap-1 hover:text-slate-600" data-testid="reprint-back-btn">
                <ChevronLeft size={13} /> Back
              </button>
            )}
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center gap-2">
              {chosen?.type === 'official_receipt' ? <ReceiptText size={16} className="text-[#1A4D2E]" /> : <FileText size={16} className="text-slate-500" />}
              <span className="text-sm font-semibold text-slate-800">{chosen?.label}</span>
            </div>

            <div>
              <p className="text-xs text-slate-500 mb-1.5">Number of copies</p>
              <div className="flex items-center justify-center gap-4">
                <Button variant="outline" size="icon" className="h-10 w-10 rounded-full" disabled={copies <= 1} onClick={() => setCopies((c) => Math.max(1, c - 1))} data-testid="reprint-copies-minus">
                  <Minus size={16} />
                </Button>
                <span className="text-3xl font-bold text-[#1A4D2E] w-12 text-center tabular-nums" data-testid="reprint-copies-count">{copies}</span>
                <Button variant="outline" size="icon" className="h-10 w-10 rounded-full" disabled={copies >= 5} onClick={() => setCopies((c) => Math.min(5, c + 1))} data-testid="reprint-copies-plus">
                  <Plus size={16} />
                </Button>
              </div>
              {copies > 1 && (
                <p className="text-[11px] text-slate-400 text-center mt-2">Prints one at a time and waits for the printer to finish each copy, then pauses 2 seconds for you to cut the paper.{chosen?.type === 'official_receipt' ? ' Copies #2+ are marked “REPRINTED COPY”.' : ''}</p>
              )}
            </div>

            <Button className="w-full h-11 bg-[#1A4D2E] hover:bg-[#143d24]" onClick={runPrint} data-testid="reprint-print-btn">
              <Printer size={16} className="mr-2" /> Print {copies > 1 ? `${copies} Copies` : 'Now'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
